import React from 'react';
import {TouchableOpacity} from 'react-native';
import Box from '@/Atoms/Box';
import CCText from '@/Atoms/CCText';
import Spacer from '@/Atoms/Spacer';
import Ionicons from 'react-native-vector-icons/Ionicons';
import CCColors, {updateColorWithOpacity} from '@/Utils/CCColors';

export type LessonFeedback = {
  status: 'idle' | 'success' | 'error' | 'branch-end';
  message?: string | null;
};

type LessonFeedbackCardProps = {
  // The coach's authored comment for the current position (null when the
  // position is a transient wrong attempt or has no annotation).
  positionComment: string | null;
  isSolved: boolean;
  lessonFeedback: LessonFeedback;
  // Authored hint on the *next* candidate move the student must still find.
  currentHint: string | undefined;
  hintRevealed: boolean;
  hasWrongAttempt: boolean;
  hasPendingMoves: boolean;
  onRevealHint: () => void;
  onViewSolution: () => void;
  onRetry: () => void;
  hasNextChapter: boolean;
  onNextChapter: () => void;
};

function LessonFeedbackCard({
  positionComment,
  isSolved,
  lessonFeedback,
  currentHint,
  hintRevealed,
  hasWrongAttempt,
  hasPendingMoves,
  onRevealHint,
  onViewSolution,
  onRetry,
  hasNextChapter,
  onNextChapter,
}: LessonFeedbackCardProps) {
  let title = 'Interactive Lesson';
  let iconName = 'information-circle-outline';
  let iconColor = CCColors.Workbook.Brown;
  // Idle state narrates the current position with the coach's own comment
  // when one is authored — matching Lichess's gamebook, where a node's
  // comment IS what the narrator says on arrival. Covers auto-played
  // opponent replies too, since they select their node and land here.
  let message =
    positionComment ||
    'Find the correct move for White/Black to solve the lesson.';

  if (isSolved) {
    title = 'Congratulations!';
    iconName = 'trophy-outline';
    iconColor = CCColors.Green;
    message =
      lessonFeedback.message || 'You have completed this lesson successfully!';
  } else if (lessonFeedback.status === 'success') {
    title = 'Correct!';
    iconName = 'checkmark-circle-outline';
    iconColor = CCColors.Green;
    message = lessonFeedback.message || 'Great job! Play the next move.';
  } else if (lessonFeedback.status === 'error') {
    title = 'Incorrect Move';
    iconName = 'alert-circle-outline';
    iconColor = CCColors.Error;
    message =
      lessonFeedback.message || 'That is not the correct move. Try again!';
  } else if (lessonFeedback.status === 'branch-end') {
    title = 'Alternate Branch';
    iconName = 'git-branch-outline';
    iconColor = CCColors.Workbook.Amber;
    message =
      lessonFeedback.message ||
      'End of this alternate branch. Now find the main line move.';
  }

  // On demand only, like Lichess's "Get a hint" button - hidden entirely
  // if the pending move has no authored hint, and never shown
  // automatically.
  const canRevealHint = Boolean(currentHint) && !isSolved;
  const canViewSolution = hasWrongAttempt && !isSolved && hasPendingMoves;

  return (
    <Box>
      {/* White card with a colored left accent + icon badge, rather than
            a solid-tinted fill - keeps the coach's message legible at any
            status color and reads as one consistent card design across
            idle/correct/incorrect/solved instead of four flat blocks. */}
      <Box
        style={{
          marginTop: 12,
          borderRadius: 14,
          backgroundColor: CCColors.White,
          borderWidth: 1,
          borderColor: updateColorWithOpacity(iconColor, 0.18),
          borderLeftWidth: 4,
          borderLeftColor: iconColor,
          shadowColor: CCColors.Workbook.DarkBrown,
          shadowOffset: {width: 0, height: 2},
          shadowOpacity: 0.06,
          shadowRadius: 8,
          elevation: 2,
          overflow: 'hidden',
        }}>
        <Box
          style={{
            flexDirection: 'row',
            alignItems: 'flex-start',
            gap: 10,
            padding: 14,
          }}>
          <Box
            style={{
              width: 32,
              height: 32,
              borderRadius: 16,
              backgroundColor: updateColorWithOpacity(iconColor, 0.14),
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}>
            <Ionicons name={iconName} size={18} color={iconColor} />
          </Box>
          <Box style={{flex: 1}}>
            <CCText fontWeight="bold" fontSize={13.5} color={iconColor}>
              {title}
            </CCText>
            <Spacer spacing={4} />
            <CCText
              fontSize={12.5}
              color={CCColors.TextColor.Primary}
              style={{lineHeight: 19}}>
              {message}
            </CCText>
            {hintRevealed && currentHint ? (
              <Box
                style={{
                  flexDirection: 'row',
                  alignItems: 'flex-start',
                  gap: 6,
                  marginTop: 8,
                  padding: 8,
                  borderRadius: 8,
                  borderWidth: 1,
                  borderStyle: 'dashed',
                  borderColor: updateColorWithOpacity(iconColor, 0.35),
                  backgroundColor: updateColorWithOpacity(iconColor, 0.06),
                }}>
                <Ionicons name="bulb-outline" size={14} color={iconColor} />
                <CCText
                  fontSize={12}
                  fontWeight="semibold"
                  color={iconColor}
                  style={{flex: 1, lineHeight: 18}}>
                  {currentHint}
                </CCText>
              </Box>
            ) : null}
          </Box>
        </Box>
      </Box>
      {(canRevealHint && !hintRevealed) || canViewSolution ? (
        <Box style={{flexDirection: 'row', gap: 8, marginTop: 8}}>
          {canRevealHint && !hintRevealed && (
            <TouchableOpacity
              onPress={onRevealHint}
              activeOpacity={0.7}
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                gap: 5,
                paddingVertical: 7,
                paddingHorizontal: 13,
                borderRadius: 20,
                borderWidth: 1,
                borderColor: CCColors.Workbook.Brown,
                backgroundColor: updateColorWithOpacity(
                  CCColors.Workbook.Brown,
                  0.05,
                ),
              }}>
              <Ionicons
                name="bulb-outline"
                size={13}
                color={CCColors.Workbook.Brown}
              />
              <CCText
                fontSize={12}
                fontWeight="bold"
                color={CCColors.Workbook.Brown}>
                Get a hint
              </CCText>
            </TouchableOpacity>
          )}
          {canViewSolution && (
            <TouchableOpacity
              onPress={onViewSolution}
              activeOpacity={0.7}
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                gap: 5,
                paddingVertical: 7,
                paddingHorizontal: 13,
                borderRadius: 20,
                borderWidth: 1,
                borderColor: CCColors.Error,
                backgroundColor: updateColorWithOpacity(CCColors.Error, 0.05),
              }}>
              <Ionicons name="eye-outline" size={13} color={CCColors.Error} />
              <CCText fontSize={12} fontWeight="bold" color={CCColors.Error}>
                View the solution
              </CCText>
            </TouchableOpacity>
          )}
        </Box>
      ) : null}
      {isSolved && (
        // Matches lila's gamebook end card (Retry / Next chapter, space
        // bar to advance - see the space-bar effect in the screen).
        <Box style={{flexDirection: 'row', gap: 8, marginTop: 8}}>
          <TouchableOpacity
            onPress={onRetry}
            activeOpacity={0.7}
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              gap: 5,
              paddingVertical: 9,
              paddingHorizontal: 16,
              borderRadius: 20,
              borderWidth: 1,
              borderColor: CCColors.Workbook.Brown,
            }}>
            <Ionicons
              name="refresh-outline"
              size={14}
              color={CCColors.Workbook.Brown}
            />
            <CCText
              fontSize={12}
              fontWeight="bold"
              color={CCColors.Workbook.Brown}>
              Retry
            </CCText>
          </TouchableOpacity>
          {hasNextChapter && (
            <TouchableOpacity
              onPress={onNextChapter}
              activeOpacity={0.85}
              style={{
                paddingVertical: 9,
                paddingHorizontal: 16,
                borderRadius: 20,
                backgroundColor: CCColors.Green,
                flexDirection: 'row',
                alignItems: 'center',
                gap: 6,
                shadowColor: CCColors.Green,
                shadowOffset: {width: 0, height: 3},
                shadowOpacity: 0.25,
                shadowRadius: 6,
                elevation: 2,
              }}>
              <CCText fontSize={12} fontWeight="bold" color={CCColors.White}>
                Next Chapter
              </CCText>
              <Ionicons name="arrow-forward" size={14} color={CCColors.White} />
            </TouchableOpacity>
          )}
        </Box>
      )}
    </Box>
  );
}

export default LessonFeedbackCard;
